import { Skeleton } from "@/components/kit";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import type { AgingStockRowDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  ReportMobileCard,
  ReportProductLink,
  ReportStat,
} from "./ReportProductLink";

type AgingStockTableProps = {
  rows?: AgingStockRowDTO[];
  isLoading?: boolean;
  minDays?: number;
  onMinDaysChange?: (days: number) => void;
  embedded?: boolean;
};

const MIN_DAY_OPTIONS = [14, 30, 60, 90, 180] as const;

function formatDays(days: number): string {
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const remain = days % 365;
    return remain > 0 ? `${years} ປີ ${remain} ວັນ` : `${years} ປີ`;
  }
  if (days >= 30) {
    const months = Math.floor(days / 30);
    const remain = days % 30;
    return remain > 0 ? `${months} ເດືອນ ${remain} ວັນ` : `${months} ເດືອນ`;
  }
  return `${days} ວັນ`;
}

function AgingStockMobileCard({ row }: { row: AgingStockRowDTO }) {
  return (
    <ReportMobileCard>
      <ReportProductLink
        productId={row.productId}
        productName={row.productName}
        variantName={row.variantName}
      />
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat
          label="ຄ້າງ"
          value={formatQtyWithUnit(row.stockQuantity, row.stockUnitLabel)}
        />
        <ReportStat
          label="ຄ້າງ (lot)"
          value={formatQtyWithUnit(row.lotRemaining, row.stockUnitLabel)}
        />
        <ReportStat
          label="lot ເກົ່າສຸດ"
          value={
            <>
              {formatDateLocal(row.oldestReceivedAt)}
              {row.oldestReceiptNumber ? (
                <span className="block text-muted-foreground text-xs">
                  {row.oldestReceiptNumber}
                </span>
              ) : null}
            </>
          }
        />
        <ReportStat
          label="ມາແລ້ວ"
          value={formatDays(row.daysInStock)}
          valueClassName="font-medium"
        />
        <ReportStat
          label="ມູນຄ່າຕົ້ນທຶນ"
          value={formatLak(row.valueAtCost)}
          className="col-span-2"
        />
      </dl>
    </ReportMobileCard>
  );
}

function AgingStockDesktopRow({ row }: { row: AgingStockRowDTO }) {
  return (
    <tr className="border-secondary/5 border-b last:border-0">
      <td className="px-4 py-2.5 sm:px-5">
        <ReportProductLink
          productId={row.productId}
          productName={row.productName}
          variantName={row.variantName}
        />
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
        {formatQtyWithUnit(row.stockQuantity, row.stockUnitLabel)}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
        {formatQtyWithUnit(row.lotRemaining, row.stockUnitLabel)}
      </td>
      <td className="px-4 py-2.5 sm:px-5">
        <div>{formatDateLocal(row.oldestReceivedAt)}</div>
        {row.oldestReceiptNumber ? (
          <div className="text-muted-foreground text-xs">
            {row.oldestReceiptNumber}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-2.5 text-right font-medium tabular-nums sm:px-5">
        {formatDays(row.daysInStock)}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
        {formatLak(row.valueAtCost)}
      </td>
    </tr>
  );
}

export function AgingStockTable({
  rows = [],
  isLoading,
  minDays = 30,
  onMinDaysChange,
  embedded = false,
}: AgingStockTableProps) {
  const filterPills =
    onMinDaysChange != null ? (
      <div className="flex flex-wrap gap-1.5">
        {MIN_DAY_OPTIONS.map((days) => (
          <button
            key={days}
            type="button"
            onClick={() => onMinDaysChange(days)}
            className={`rounded-full px-3 py-1 text-xs transition-colors ${
              minDays === days
                ? "bg-secondary/20 font-medium text-foreground"
                : "bg-muted/50 text-muted-foreground hover:bg-muted"
            }`}
          >
            ≥{days}d
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className="brand-card overflow-hidden">
      {embedded ? (
        filterPills ? (
          <div className="border-secondary/15 border-b px-4 py-3 sm:px-5">
            {filterPills}
          </div>
        ) : null
      ) : (
        <div className="flex flex-col gap-3 border-secondary/15 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 className="font-display text-lg">ສິນຄ້າຄ້າງສະຕັອກນານ</h2>
            <p className="text-muted-foreground text-sm">
              lot ເກົ່າສຸດຄ້າງຢູ່ — ນັບຈາກວັນຮັບເຂົ້າ
            </p>
          </div>
          {filterPills}
        </div>
      )}
      {isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          {["a", "b", "c", "d", "e"].map((id) => (
            <Skeleton key={id} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-muted-foreground text-sm">
          ບໍ່ມີສິນຄ້າຄ້າງເກີນ {minDays} ວັນ
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3 p-4 md:hidden">
            {rows.map((row) => (
              <AgingStockMobileCard key={row.variantId} row={row} />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-secondary/10 border-b text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium sm:px-5">ສິນຄ້າ</th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຄ້າງ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຄ້າງ (lot)
                  </th>
                  <th className="px-4 py-2 font-medium sm:px-5">lot ເກົ່າສຸດ</th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ມາແລ້ວ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ມູນຄ່າຕົ້ນທຶນ
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <AgingStockDesktopRow key={row.variantId} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
